import { React, useEffect, useState } from 'react';
import BootstrapForm from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { ReactComponent as companyFormIcon } from '../../../../../assets/images/CompanyFormIcon.svg';
import { ReactComponent as jobTitleFormIcon } from '../../../../../assets/images/JobTitleFormIcon.svg';
import Button from '../../../../../components/shared/Button/Button';
import CustomTextAreaField from '../../../../../components/shared/CustomTextAreaField/CustomTextAreaField';
import CustomTextField from '../../../../../components/shared/CustomTextField/CustomTextField';
import LocationSelect from '../../../../../components/shared/Location/LocationSelect';
import Modal from '../../../../../components/shared/Modal/Modal';
import StartEndYearFields from '../../../../../components/shared/StartEndYearFields/StartEndYearFields';
import { FORM_CONSTANTS, FORM_MESSAGES } from '../../../../../constants/commonConstants';
import { IsMobile } from '../../../../../helpers/IsMobile';

import styles from './AddExperienceModal.module.scss';

const ExperienceFormSchema = Yup.object().shape({
  job_title: Yup.string()
    .min(FORM_CONSTANTS.MIN_LENGTH, FORM_MESSAGES.TOO_SHORT)
    .max(FORM_CONSTANTS.MAX_LENGTH, FORM_MESSAGES.TOO_LONG)
    .required(),
  company: Yup.string()
    .min(FORM_CONSTANTS.MIN_LENGTH, FORM_MESSAGES.TOO_SHORT)
    .max(FORM_CONSTANTS.MAX_LENGTH, FORM_MESSAGES.TOO_LONG)
    .required(),
  location: Yup.string(),
  description: Yup.string().min(FORM_CONSTANTS.MIN_LENGTH, FORM_MESSAGES.TOO_SHORT),
  current_position: Yup.boolean(),
  start_year: Yup.string().required(),
  end_year: Yup.string()
    .nullable()
    .when('current_position', {
      is: (v) => !!v === false,
      then: Yup.string().required(),
    }),
});

const INITIAL_VALUES = {
  job_title: '',
  company: '',
  location: '',
  start_year: '',
  end_year: '',
  current_position: false,
  description: '',
};

const AddExperienceModal = ({ showModal, setShowModal, initialValues = {}, handleSave, formData }) => {
  const { t } = useTranslation('common');
  const [formValues, setFormValues] = useState(INITIAL_VALUES);
  const [isEdit, setIsEdit] = useState(false);
  const modalFullScreen = IsMobile();

  useEffect(() => {
    const data = initialValues;
    if (Object.keys(data).length) {
      setIsEdit(true);
      if (!data.description) {
        data.description = '';
      }
      setFormValues(data);
    } else {
      setFormValues({});
      setIsEdit(false);
    }
  }, [initialValues]);

  const handleSubmit = (data) => {
    const changedFormData = [...formData];
    const submitedValues = data;
    if (submitedValues.currentPosition) {
      submitedValues.endYear = '';
    }
    if (data !== initialValues) {
      const index = changedFormData.indexOf(initialValues);
      if (index > -1) {
        changedFormData.splice(initialValues, 1);
      }
      changedFormData.push(data);
      handleSave(changedFormData);
    } else {
      setShowModal(false);
    }
  };

  const handleDelete = (data) => {
    if (isEdit) {
      const newData = [...formData].map((education) =>
        data.id === education.id ? { ...education, _destroy: true } : education
      );
      handleSave(newData);
    } else {
      setShowModal(false);
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={() => setShowModal(false)}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      fullscreen={modalFullScreen}
    >
      <Modal.CustomHeader closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          {isEdit ? t('profile.editExperience') : t('profile.addNewExperience')}
        </Modal.Title>
      </Modal.CustomHeader>
      <Modal.Body>
        <div className={modalFullScreen ? styles.phoneBody : styles.body}>
          <Formik
            initialValues={formValues}
            onSubmit={handleSubmit}
            enableReinitialize
            validationSchema={ExperienceFormSchema}
          >
            {({ values, setValues, dirty, isValid }) => (
              <Form>
                <div className={modalFullScreen && styles.phoneContainer}>
                  <div>
                    <Field
                      name="job_title"
                      as={CustomTextField}
                      type="text"
                      label={t('labels.jobTitle')}
                      className={styles.inputField}
                      CustomIcon={jobTitleFormIcon}
                    />
                    <Field
                      name="company"
                      as={CustomTextField}
                      type="text"
                      label={t('labels.company')}
                      className={styles.inputField}
                      CustomIcon={companyFormIcon}
                    />
                    <Field
                      name="organization_location_params"
                      as={LocationSelect}
                      type="text"
                      label={t('labels.location')}
                      onChange={(location) => {
                        setValues({
                          ...values,
                          location: location.full_address,
                        });
                      }}
                      value={{ full_address: values.location }}
                    />
                    <StartEndYearFields />
                    <div className={styles.checkboxContainer}>
                      <Field
                        name="current_position"
                        as={BootstrapForm.Check}
                        isValid
                        checked={values.current_position}
                      />
                      <span className={styles.currentPosition}>{t('profile.currentPosition')}</span>
                    </div>
                    <Field
                      name="description"
                      as={CustomTextAreaField}
                      type="text-area"
                      label={t('description')}
                      className={styles.customTextField}
                    />
                  </div>
                  <div className={modalFullScreen ? styles.phoneButtonContainer : styles.buttonContainer}>
                    <Button
                      variant="secondary"
                      className={modalFullScreen ? styles.phoneButton : styles.button}
                      onClick={() => handleDelete(values)}
                    >
                      {isEdit ? t('delete') : t('cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      className={modalFullScreen ? styles.phoneButton : styles.button}
                      disabled={!(isValid && dirty)}
                    >
                      {t('save')}
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AddExperienceModal;
